import React, {
  ChangeEvent, createContext, useCallback, useContext, useEffect, useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import omit from 'lodash/omit';
import set from 'lodash/set';
import IAutocompleteEvent from '../components/interfaces/IAutocompleteEvent';
import IOrder, { IQuantities } from './interfaces/IOrder';
import { DeliveryMethod, MakeOptional } from '../generated/graphql';
import { Address } from '../components/GooglePlacesComponent';
import { useLocalStorage } from '../utils/hooks/localstorage.hook';
import { isDefined, isRegularArticle, omitArticleIds } from '../utils/helpers';
import { CampaignContext } from './CampaignWrapper';

/*
  The value passed to a context gets updated with changes.
  We need to access the original defaults in resetOrder.
  That's why we start with a variable that should remain unchanged, and duplicate it before we pass it to the context.
*/
const IMMUTABLE_ORDER_DEFAULTS = {
  order: {
    name: '',
    email: '',
    quantities: {},
    deliveryMethod: undefined,
    address: undefined,
    customFields: {},
  },
  articleCount: 0,
  setPurchaserInfo: () => {},
  setArticleQuantity: () => {},
  removeArticleFromCart: () => {},
  setCustomField: () => {},
  setOrder: () => {},
  resetOrder: () => {},
} as const;

const orderContextDefaults = JSON.parse(JSON.stringify(IMMUTABLE_ORDER_DEFAULTS));

export type Order = Omit<MakeOptional<IOrder, 'deliveryMethod'>, 'address'> & { address?: Address };

interface IOrderContext {
  order: Order
  setOrder: React.Dispatch<React.SetStateAction<Order>>
  resetOrder: () => void
  articleCount: number
  setPurchaserInfo: (e: { target: { name: string, value: string | Address } }) => void
  setArticleQuantity: (articleId: string, count: number) => void
  removeArticleFromCart: (articleId: string) => void
  setCustomField: (e: ChangeEvent<HTMLInputElement> | IAutocompleteEvent) => void
}

export const OrderContext = createContext<IOrderContext>(orderContextDefaults);

const OrderContextProvider = ({ children } : { children: React.ReactNode }) => {
  const { slug } = useParams();
  const {
    campaign: {
      articles, isSellingEnabled, deliveryOptions, timeslots, formFields,
    } = {
      articles: undefined, isSellingEnabled: true, deliveryOptions: undefined, timeslots: undefined, formFields: undefined,
    },
  } = useContext(CampaignContext);
  const [order, setOrder] = useLocalStorage<Order>(`order.${slug}`, orderContextDefaults.order);
  const shippingCostsArticleId = articles?.find((a) => a.__typename === 'ShippingCostsArticle')?._id;

  const removeArticleFromCart = useCallback((articleId: string) => {
    const regularArticleIds = articles?.filter(isRegularArticle).map((a) => a._id);
    const otherRegularQuantities = Object
      .entries(order.quantities)
      .filter(([key]) => key !== articleId && regularArticleIds?.includes(key));
    const isLastRegularArticle = otherRegularQuantities.reduce((total, [, quantity]) => total + quantity, 0) === 0;
    setOrder((o) => ({
      ...o,
      quantities: omitArticleIds(o.quantities, [articleId, shippingCostsArticleId]),
      ...(isLastRegularArticle ? {
        deliveryMethod: undefined,
      } : {}),
    }));
  }, [articles, order.quantities, setOrder, shippingCostsArticleId]);

  // Auto-remove non-existing articles (e.g. disabled or removed ones)
  useEffect(() => {
    if (articles) {
      const nonExistingArticleIds = Object
        .keys(order.quantities || {})
        .filter((articleId) => !articles?.some(({ _id }) => articleId === _id.toString()));
      if (nonExistingArticleIds.length) nonExistingArticleIds.forEach((id) => removeArticleFromCart(id));
    }
  }, [articles, order.quantities, removeArticleFromCart]);

  // Auto-remove irrelevant order properties
  useEffect(() => {
    if (!deliveryOptions?.deliveryMethods.length) {
      setOrder((o) => omit(o, 'deliveryMethod'));
    }
  }, [deliveryOptions?.deliveryMethods, setOrder]);

  useEffect(() => {
    if (deliveryOptions?.deliveryMethods
      && !(deliveryOptions?.deliveryMethods as DeliveryMethod[]).includes(DeliveryMethod.Deliver)) {
      setOrder((o) => omit(o, 'address'));
    }
  }, [deliveryOptions?.deliveryMethods, order.address, setOrder]);

  useEffect(() => {
    if (isDefined(order.collectTimeslot)) {
      if ((!order.deliveryMethod
        && (timeslots?.collect?.length || 0) + (timeslots?.deliver?.length || 0) + (timeslots?.eatIn?.length || 0) === 0)
        || (order.deliveryMethod === DeliveryMethod.Collect && !timeslots?.collect?.length)
        || (order.deliveryMethod === DeliveryMethod.Deliver && !timeslots?.deliver?.length)
        || (order.deliveryMethod === DeliveryMethod.EatIn && !timeslots?.eatIn?.length)
      ) {
        setOrder((o) => omit(o, 'collectTimeslot'));
      }
    }
  }, [timeslots, order.collectTimeslot, order.deliveryMethod, setOrder]);

  useEffect(() => {
    Object.keys(order.customFields).forEach((key) => {
      if (!formFields?.find((f) => f._id === key)) {
        setOrder((o) => set(o, 'customFields', omit(o.customFields, key)));
      }
    });
  }, [formFields, order.customFields, setOrder]);

  const orderContext = useMemo(() => {
    const articleCount = !isSellingEnabled ? 0 : Object
      .entries(order.quantities as IQuantities)
      .filter(([articleId]) => articles?.some((a) => isRegularArticle(a) && a._id === articleId))
      .reduce((sum, [, quantity]) => sum + quantity, 0);

    const setPurchaserInfo = (e: { target: { name: string, value: string | Address } }) => {
      const { name, value } = e.target;

      setOrder((o) => ({
        ...o,
        [name]: value,
      }));
    };

    const setCustomField = (e: ChangeEvent<HTMLInputElement> | IAutocompleteEvent) => {
      const { name, value: v } = e.target;
      const value = [v].flat();

      setOrder((o) => ({
        ...o,
        customFields: {
          ...o.customFields,
          [name]: value,
        },
      }));
    };

    const setArticleQuantity = (articleId: string, quantity: number) => {
      const deliveryMethod = deliveryOptions?.deliveryMethods?.length === 1 ? deliveryOptions?.deliveryMethods[0] : undefined;
      const shippingCostArticleNecessary = deliveryMethod === 'deliver' && shippingCostsArticleId;
      if (order.quantities[articleId] === quantity
        && (!shippingCostArticleNecessary || order.quantities[shippingCostsArticleId] === 1)) return;

      setOrder((o) => ({
        ...o,
        quantities: {
          ...o.quantities,
          [articleId]: quantity,
          ...(shippingCostArticleNecessary ? {
            [shippingCostsArticleId]: 1,
          } : {}),
        },
      }));
    };

    const resetOrder = () => {
      setOrder(IMMUTABLE_ORDER_DEFAULTS.order);
    };

    return {
      order,
      setOrder,
      articleCount,
      setPurchaserInfo,
      setArticleQuantity,
      removeArticleFromCart,
      setCustomField,
      resetOrder,
    };
  }, [
    isSellingEnabled, order, setOrder, removeArticleFromCart, articles, deliveryOptions?.deliveryMethods, shippingCostsArticleId,
  ]);

  return (
    <OrderContext.Provider value={orderContext}>
      {children}
    </OrderContext.Provider>
  );
};

export default OrderContextProvider;
